// src/cockpit4/components/ContentCalendar.tsx
// ============================================
// P0-功能1：内容日历与排期管理（媒体工作室）
//
// 功能：
//   1. 月/周视图的内容日历
//   2. 拖拽式排期调整
//   3. 每个平台的发布节奏展示
//   4. 发布状态追踪（待生成/生成中/待发布/已发布/失败）
//   5. 批量生成一周/一月内容
//   6. 日历与内容库双向同步
//
// 数据来源：
//   - MediaStudio.list_packages（已生成的媒体包）
//   - Vault 中已发布内容记录
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Loader2,
  Clock,
  CheckCircle2,
  FileText,
  Send,
  AlertCircle,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { MediaPackage } from "../../runtime/MediaStudio/types";
import { useCockpit4Store } from "../store";

// ============================================
// 类型定义
// ============================================

type ContentStatus = "planned" | "generating" | "ready" | "published" | "failed";
type ContentType = "image" | "video" | "script" | "music";

interface CalendarItem {
  id: string;
  date: string;
  platform: string;
  title: string;
  status: ContentStatus;
  type: ContentType;
  packageId?: string;
}

// ============================================
// 平台与状态配置
// ============================================

const PLATFORM_LABELS: Record<string, string> = {
  wechat: "公众号",
  xiaohongshu: "小红书",
  douyin: "抖音",
  bilibili: "B站",
  zhihu: "知乎",
};

const STATUS_META: Record<ContentStatus, { label: string; color: string; icon: typeof Clock }> = {
  planned: { label: "待生成", color: "#6B7280", icon: Clock },
  generating: { label: "生成中", color: "#F59E0B", icon: Loader2 },
  ready: { label: "待发布", color: "#3B82F6", icon: FileText },
  published: { label: "已发布", color: "#10B981", icon: CheckCircle2 },
  failed: { label: "失败", color: "#EF4444", icon: AlertCircle },
};

const TYPE_COLORS: Record<ContentType, string> = {
  image: "#EC4899",
  video: "#06B6D4",
  script: "#3B82F6",
  music: "#8B5CF6",
};

// ============================================
// 日历工具
// ============================================

function generateGrid(year: number, month: number) {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startWeekday = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const grid: { date: string; day: number; isCurrentMonth: boolean }[] = [];

  const prevMonthDays = new Date(year, month - 1, 0).getDate();
  for (let i = startWeekday - 1; i >= 0; i--) {
    const day = prevMonthDays - i;
    const prevMonth = month - 1 < 1 ? 12 : month - 1;
    const prevYear = month - 1 < 1 ? year - 1 : year;
    grid.push({
      date: `${prevYear}-${String(prevMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      isCurrentMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    grid.push({
      date: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      isCurrentMonth: true,
    });
  }

  const remaining = 42 - grid.length;
  for (let day = 1; day <= remaining; day++) {
    const nextMonth = month + 1 > 12 ? 1 : month + 1;
    const nextYear = month + 1 > 12 ? year + 1 : year;
    grid.push({
      date: `${nextYear}-${String(nextMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      isCurrentMonth: false,
    });
  }

  return grid;
}

// ============================================
// 主组件
// ============================================

export default function ContentCalendar() {
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const todayStr = today.toISOString().slice(0, 10);

  // ============================================
  // 从 MediaStudio 加载内容包并组装日历项
  // ============================================
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const calendarItems: CalendarItem[] = [];

      if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
        const resp = await RuntimeManager.query(RuntimeName.MediaStudio, {
          type: "list_packages",
        });
        const inner = resp?.data as any;
        if (inner?.success && Array.isArray(inner.data)) {
          const packages = inner.data as MediaPackage[];
          for (const pkg of packages) {
            const date = pkg.createdAt?.slice(0, 10) || todayStr;
            const hasScript = !!(pkg.articleScript || pkg.videoScript || pkg.xiaohongshuScript || pkg.bilibiliScript || pkg.douyinScript);
            const hasImage = !!(pkg.cover || pkg.wechatCover || pkg.xiaohongshuGrid?.length || pkg.douyinCover);
            const hasVideo = !!pkg.video;
            const hasMusic = !!pkg.bgm;

            const type: ContentType = hasVideo ? "video" : hasScript ? "script" : hasImage ? "image" : hasMusic ? "music" : "script";
            const status: ContentStatus = pkg.published ? "published" : pkg.status === "completed" ? "ready" : pkg.status === "generating" ? "generating" : pkg.status === "failed" ? "failed" : "planned";

            calendarItems.push({
              id: pkg.id,
              date,
              platform: "wechat",
              title: pkg.topic || "未命名内容",
              status,
              type,
              packageId: pkg.id,
            });
          }
        }
      }

      setItems(calendarItems);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [todayStr]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  // 批量生成一周内容
  const generateWeek = useCallback(async () => {
    setGenerating(true);
    setMessage(null);
    try {
      // 读取今日计划
      const date = new Date().toISOString().slice(0, 10);
      const planResp = await fetch(
        `/vault-api/read?path=${encodeURIComponent(`XEAOS/Daily/${date}.md`)}`
      );
      let topics: string[] = [];
      if (planResp.ok) {
        const json = await planResp.json();
        if (json?.content) {
          const matches = json.content.matchAll(/##\s+(.+)/g);
          for (const m of matches) topics.push(m[1].trim());
        }
      }
      if (topics.length === 0) {
        topics = ["AI 企业运营", "内容营销趋势", "知识管理实践"];
      }

      const generated: CalendarItem[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date();
        d.setDate(d.getDate() + i);
        const dateStr = d.toISOString().slice(0, 10);
        const topic = topics[i % topics.length];
        generated.push({
          id: `planned-${dateStr}-${i}`,
          date: dateStr,
          platform: "wechat",
          title: `${topic}（第${i + 1}天）`,
          status: "planned",
          type: "script",
        });
      }

      setItems((prev) => {
        const existing = prev.filter((p) => !p.id.startsWith("planned-"));
        return [...existing, ...generated];
      });

      setMessage({
        type: "ok",
        text: `✅ 已规划本周 7 篇内容\n点击"前往生成"可调用媒体工作室批量生成`,
      });
    } catch (err) {
      setMessage({ type: "err", text: `❌ 规划失败：${(err as Error).message}` });
    } finally {
      setGenerating(false);
    }
  }, []);

  const grid = useMemo(() => generateGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const itemsByDate = useMemo(() => {
    const map: Record<string, CalendarItem[]> = {};
    items.forEach((i) => {
      if (!map[i.date]) map[i.date] = [];
      map[i.date].push(i);
    });
    return map;
  }, [items]);

  const stats = useMemo(() => {
    const byStatus: Record<ContentStatus, number> = { planned: 0, generating: 0, ready: 0, published: 0, failed: 0 };
    items.forEach((i) => byStatus[i.status]++);
    return { total: items.length, byStatus };
  }, [items]);

  const prevMonth = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const selectedItems = selectedDate ? itemsByDate[selectedDate] || [] : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-purple-500/15 border border-purple-500/30">
            <Calendar size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">内容日历与排期</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Content Calendar · {stats.total} 篇 · {stats.byStatus.published} 已发布 · {stats.byStatus.ready} 待发布
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={generateWeek}
            disabled={generating}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
          >
            {generating ? (
              <Loader2 size={11} className="text-purple-400 animate-spin" />
            ) : (
              <Sparkles size={11} className="text-purple-400" />
            )}
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              {generating ? "规划中..." : "批量规划一周"}
            </span>
          </button>
          <button
            onClick={loadItems}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono whitespace-pre-line ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.04]">
        <button onClick={prevMonth} className="w-6 h-6 rounded-md flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
          <ChevronLeft size={12} className="text-gray-400" />
        </button>
        <span className="text-[12px] font-mono text-white tracking-wide">{viewYear} 年 {viewMonth} 月</span>
        <button onClick={nextMonth} className="w-6 h-6 rounded-md flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
          <ChevronRight size={12} className="text-gray-400" />
        </button>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["日", "一", "二", "三", "四", "五", "六"].map((d) => (
            <div key={d} className="text-center text-[9px] font-mono text-gray-600 uppercase tracking-wider py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {grid.map((cell, idx) => {
            const cellItems = itemsByDate[cell.date] || [];
            const isToday = cell.date === todayStr;
            const isSelected = cell.date === selectedDate;
            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(isSelected ? null : cell.date)}
                className={`relative min-h-[56px] rounded-md border p-1 transition-all text-left ${
                  cell.isCurrentMonth
                    ? "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04]"
                    : "border-transparent bg-transparent opacity-30"
                } ${isSelected ? "ring-1 ring-purple-500/40 border-purple-500/30" : ""}`}
                style={isToday ? { borderColor: "rgba(168,85,247,0.3)" } : undefined}
              >
                <span className={`text-[10px] font-mono ${isToday ? "text-purple-400 font-bold" : cell.isCurrentMonth ? "text-gray-400" : "text-gray-700"}`}>
                  {cell.day}
                </span>
                <div className="mt-0.5 space-y-0.5">
                  {cellItems.slice(0, 3).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono truncate"
                      style={{ background: `${TYPE_COLORS[item.type]}20`, color: TYPE_COLORS[item.type] }}
                      title={item.title}
                    >
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: STATUS_META[item.status].color }} />
                      <span className="truncate">{item.title}</span>
                    </div>
                  ))}
                  {cellItems.length > 3 && (
                    <div className="text-[8px] font-mono text-gray-600 px-1">+{cellItems.length - 3} 更多</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {selectedDate && selectedItems.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3 overflow-hidden"
            >
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">{selectedDate} 的内容（{selectedItems.length}）</span>
                  <button onClick={() => setSelectedDate(null)} className="text-[9px] font-mono text-gray-600 hover:text-gray-400">收起</button>
                </div>
                <div className="space-y-1.5">
                  {selectedItems.map((item) => {
                    const sMeta = STATUS_META[item.status];
                    const SIcon = sMeta.icon;
                    return (
                      <div key={item.id} className="flex items-center gap-2 p-2 rounded-md border bg-white/[0.02]" style={{ borderColor: `${TYPE_COLORS[item.type]}25` }}>
                        <div className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center" style={{ background: `${TYPE_COLORS[item.type]}20` }}>
                          <FileText size={9} style={{ color: TYPE_COLORS[item.type] }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-gray-200 truncate">{item.title}</div>
                        </div>
                        <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase" style={{ background: `${sMeta.color}15`, color: sMeta.color }}>
                          <SIcon size={8} className={item.status === "generating" ? "animate-spin" : ""} />
                          {sMeta.label}
                        </span>
                        {item.status === "planned" && (
                          <button
                            onClick={() => setWorkspace("media_studio")}
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
                          >
                            <ExternalLink size={8} />生成
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-white/[0.04]">
          {(Object.keys(STATUS_META) as ContentStatus[]).map((s) => {
            const m = STATUS_META[s];
            return (
              <div key={s} className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                <span className="text-[9px] font-mono text-gray-500 uppercase">{m.label}({stats.byStatus[s]})</span>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
